import { CompanyInfoBanner, useCompanyLabel, useCompanyLogo } from "@/components/CompanyInfoBanner";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Timer, FilePdf } from "@phosphor-icons/react";
import { fmtBRL, fmtHours, fmtDT } from "@/components/DetentionDialog";
import jsPDF from "jspdf";

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

export default function Detentions() {
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState({ rows: [], totals: {} });
  const companyLabel = useCompanyLabel();
  const logo = useCompanyLogo();
  const load = () => api.get("/detentions/report", { params: { start, end } }).then((r) => setData(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const pdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    if (logo) doc.addImage(logo.url, logo.format, 297 - 14 - Math.min(40, 14 * logo.ratio), 6, Math.min(40, 14 * logo.ratio), 14);
    doc.setFontSize(16); doc.text("Relatório de Estadias", 14, 16);
    doc.setFontSize(10); if (companyLabel) doc.text(`Empresa: ${companyLabel}`, 140, 23);
    doc.text(`Período: ${start.split("-").reverse().join("/")} a ${end.split("-").reverse().join("/")}`, 14, 23);
    let y = 32;
    const cols = [["Cliente", 14], ["Rota", 64], ["Placa", 124], ["Motorista", 146], ["Chegada", 186], ["Saída", 214], ["Parado", 242], ["Cobrável", 260], ["Valor", 278]];
    doc.setFont(undefined, "bold"); cols.forEach(([t, x]) => doc.text(t, x, y)); doc.setFont(undefined, "normal");
    data.rows.forEach((r) => {
      y += 7; if (y > 195) { doc.addPage(); y = 16; }
      const vals = [r.client, r.route, r.plate, r.driver_name, fmtDT(r.arrived_at), fmtDT(r.left_at), fmtHours(r.hours), fmtHours(r.billable_hours), fmtBRL(r.value)];
      vals.forEach((v, i) => doc.text(String(v || "").slice(0, i === 1 ? 32 : 24), cols[i][1], y));
    });
    y += 10; doc.setFont(undefined, "bold");
    doc.text(`Total: ${data.totals.count || 0} estadias · ${fmtHours(data.totals.hours)} paradas · ${fmtHours(data.totals.billable_hours)} cobráveis · ${fmtBRL(data.totals.value)}`, 14, y);
    doc.save(`estadias-${start}-a-${end}.pdf`);
  };

  return (
    <div className="space-y-6">
      <CompanyInfoBanner />
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
          <Timer size={32} weight="duotone" className="text-blue-600" /> Estadias
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Tempo que cada caminhão ficou parado no cliente — para cobrar a estadia</p>
      </div>
      <Card>
        <CardContent className="pt-6 flex flex-wrap items-end gap-3">
          <div><Label>De</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="detentions-start" /></div>
          <div><Label>Até</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="detentions-end" /></div>
          <Button onClick={load} data-testid="btn-detentions-filter" className="bg-blue-600 hover:bg-blue-700 text-white">Filtrar</Button>
          <Button variant="outline" onClick={pdf} disabled={!data.rows.length} data-testid="btn-detentions-pdf" className="gap-1.5"><FilePdf size={16} /> Exportar PDF</Button>
        </CardContent>
      </Card>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Estadias", data.totals.count || 0], ["Tempo parado", fmtHours(data.totals.hours)], ["Horas cobráveis", fmtHours(data.totals.billable_hours)], ["Valor a cobrar", fmtBRL(data.totals.value)]].map(([l, v]) => (
          <Card key={l}><CardContent className="pt-5"><div className="text-xs text-slate-500">{l}</div><div className="text-xl font-extrabold text-slate-900" data-testid={`detentions-total-${l}`}>{v}</div></CardContent></Card>
        ))}
      </div>
      <Card>
        <CardHeader><CardTitle className="text-lg">Registros</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {data.rows.length === 0 ? <p className="text-sm text-slate-500">Nenhuma estadia no período. Ative o controle de estadia no frete (botão de relógio na lista de Fretes).</p> : (
            <Table>
              <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Rota</TableHead><TableHead>Placa</TableHead><TableHead>Motorista</TableHead><TableHead>Chegada</TableHead><TableHead>Saída</TableHead><TableHead className="text-right">Parado</TableHead><TableHead className="text-right">Cobrável</TableHead><TableHead className="text-right">Valor</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.id} data-testid={`detention-report-row-${r.id}`}>
                    <TableCell className="font-semibold">{r.client}</TableCell>
                    <TableCell className="text-xs">{r.route}</TableCell>
                    <TableCell className="text-xs">{r.plate}</TableCell>
                    <TableCell className="text-xs">{r.driver_name}</TableCell>
                    <TableCell className="text-xs">{fmtDT(r.arrived_at)}</TableCell>
                    <TableCell className="text-xs">{r.open ? <span className="text-amber-700 font-semibold">contando</span> : fmtDT(r.left_at)}</TableCell>
                    <TableCell className="text-right">{fmtHours(r.hours)}</TableCell>
                    <TableCell className="text-right">{fmtHours(r.billable_hours)}</TableCell>
                    <TableCell className="text-right font-bold text-emerald-700">{fmtBRL(r.value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
